import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { kioskApi } from '../lib/kiosk-api.js';

// ─── Knowledge Drip card (KNOWLEDGE-DRIP-SPEC.md Phase 2) ────────────────
// One bite-size knowledge card per person per day: a fact with a "Got it",
// or (about 1 in 5) a one-tap question. Quiet by design (the Availability
// Health principle): renders nothing when drip is off, when there's no
// card, when today's card was already acked on either surface, or when
// the KB is unreachable. Never a streak, never a nag, never blocking.
//
// mode="home"  — phone/desktop Home stack; auths with x-user-id like api().
// mode="kiosk" — post-punch moment on the iPad; auths with the kiosk token.
export function KnowledgeDripCard({ mode = 'home', user, session, onDone }) {
  const [card, setCard] = useState(null);
  const [firstCard, setFirstCard] = useState(false);
  const [result, setResult] = useState(null); // { correct, answer } after a question tap
  const [acked, setAcked] = useState(false);
  const [busy, setBusy] = useState(false);

  const call = (method, path, body) =>
    mode === 'kiosk'
      ? kioskApi(method, path.replace('/api/drip/', '/api/kiosk/drip/'), body, session.token)
      : api(method, path, body, { 'x-user-id': String(user.id) });

  useEffect(() => {
    let live = true;
    call('GET', '/api/drip/today', null)
      .then((r) => {
        if (live && r?.enabled && r.card && !r.card.seen_today) {
          setCard(r.card);
          setFirstCard(!!r.first_card);
        }
      })
      .catch(() => {}); // quiet: no card beats an error banner
    return () => { live = false; };
  }, []);

  if (!card) return null;

  async function ack(answer) {
    if (busy) return;
    setBusy(true);
    try {
      const r = await call('POST', '/api/drip/ack', { atom_slug: card.slug, ...(answer !== undefined ? { answer } : {}) });
      if (card.kind === 'question') {
        setResult({ correct: r.correct, answer: r.answer });
        setTimeout(() => { setAcked(true); if (onDone) onDone(); }, mode === 'kiosk' ? 6000 : 8000);
      } else {
        setAcked(true);
        if (onDone) onDone();
      }
    } catch {
      // Ack failed (KB blip): leave the card up; tomorrow it re-offers.
      setAcked(true);
      if (onDone) onDone();
    } finally {
      setBusy(false);
    }
  }

  if (acked) return null;

  const btn = {
    padding: '10px 16px', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
    background: '#fff', color: '#1B2B1B', border: '1px solid #d1d5db',
    borderRadius: 8, cursor: 'pointer', textAlign: 'left',
  };

  return (
    <div className="card" style={{ borderLeft: '4px solid var(--gold, #C9A227)', maxWidth: mode === 'kiosk' ? 420 : undefined }}>
      <div style={{ fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
        {card.kind === 'question' ? 'Quick check · just practice — not a real event' : 'One thing today'}
      </div>
      {firstCard && (
        <div style={{ fontSize: 12, color: '#757575', marginBottom: 8, fontStyle: 'italic' }}>
          New: one card a day from Empire's playbook — a quick refresher or a
          practice question, never a live instruction. Tap and carry on.
        </div>
      )}
      {card.kind === 'question' && (
        <div style={{ fontSize: 11, color: '#92400e', fontWeight: 600, marginBottom: 4 }}>
          Imagine this scenario:
        </div>
      )}
      <div style={{ fontSize: 15, color: '#212121', lineHeight: 1.5, marginBottom: 10 }}>{card.card_md}</div>

      {result ? (
        <div style={{ fontSize: 14, fontWeight: 600, color: result.correct ? '#166534' : '#92400e' }}>
          {result.correct ? `✓ That's it — ${result.answer}` : `It's: ${result.answer}. No sweat — we'll ask again sometime.`}
        </div>
      ) : card.kind === 'question' && Array.isArray(card.options) ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {card.options.map((o) => (
            <button key={o} onClick={() => ack(o)} disabled={busy} style={btn}>{o}</button>
          ))}
        </div>
      ) : (
        <button onClick={() => ack()} disabled={busy}
          style={{ ...btn, background: 'var(--forest, #1B2B1B)', color: '#fff', border: 'none', textAlign: 'center' }}>
          Got it
        </button>
      )}

      {card.doc_title && (
        <div style={{ fontSize: 11, color: '#9e9e9e', marginTop: 8 }}>From: {card.doc_title}</div>
      )}
    </div>
  );
}
